import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragOverlay,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAuth } from "../auth/AuthContext";
import {
  COLUMNS,
  createTask,
  deleteTask,
  reorderTasks,
  subscribeTasks,
  updateTask,
} from "../projects/tasks";
import "./kanban.css";

const Card = ({ task, draggable, canEdit, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: !draggable,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: draggable ? "grab" : "default",
  };
  const isBug = task.column === "bugs";
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`fw-kan__card${isBug ? " fw-kan__card--bug" : ""}`}
      {...attributes}
      {...(draggable ? listeners : {})}
    >
      {isBug && <span className="fw-kan__card-tag">Bug</span>}
      <div className="fw-kan__card-title">{task.title}</div>
      {task.description && <div className="fw-kan__card-desc">{task.description}</div>}
      {canEdit && (
        <div className="fw-kan__card-actions">
          <button type="button" onClick={(e) => { e.stopPropagation(); onEdit?.(task); }}>Edit</button>
          <button type="button" onClick={(e) => { e.stopPropagation(); onDelete?.(task); }}>Delete</button>
        </div>
      )}
    </div>
  );
};

const Column = ({ column, tasks, isAdmin, canWrite, currentUid, onAdd, onEdit, onDelete }) => {
  const ids = tasks.map((t) => t.id);
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const isBugs = column.id === "bugs";
  // Studio admins add anywhere; clients may log bugs only if they can act
  // (owner/editor). Viewers are read-only.
  const canAdd = isAdmin || (isBugs && canWrite);
  const addLabel = isBugs ? "+ Log bug" : "+ Add card";
  return (
    <div className="fw-kan__col" data-col={column.id}>
      <div className="fw-kan__col-head">
        <span className={`fw-kan__col-label${isBugs ? " fw-kan__col-label--bug" : ""}`}>{column.label}</span>
        <span className="fw-kan__col-count">{tasks.length}</span>
      </div>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={`fw-kan__col-body ${isOver ? "is-over" : ""}`}
          data-column-drop={column.id}
        >
          {tasks.map((t) => {
            const owns = t.authorUid && t.authorUid === currentUid;
            const draggable = isAdmin;
            const canEdit = isAdmin || (owns && canWrite);
            return (
              <Card
                key={t.id}
                task={t}
                draggable={draggable}
                canEdit={canEdit}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            );
          })}
          {tasks.length === 0 && (
            <div className="fw-kan__col-empty">
              {canAdd ? "Drop here" : "—"}
            </div>
          )}
        </div>
      </SortableContext>
      {canAdd && (
        <button type="button" className="fw-kan__col-add" onClick={() => onAdd(column.id)}>
          {addLabel}
        </button>
      )}
    </div>
  );
};

const TaskModal = ({ initial, onSave, onClose }) => {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave({ title: title.trim(), description: description.trim() });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fw-kan__modal-backdrop" onClick={onClose}>
      <form className="fw-kan__modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3 style={{ margin: 0 }}>{initial?.id ? "Edit card" : "New card"}</h3>
        <div className="fw-field">
          <label>Title</label>
          <input className="fw-input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus required />
        </div>
        <div className="fw-field">
          <label>Description</label>
          <textarea className="fw-textarea" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="fw-btn fw-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="fw-btn fw-btn--primary" disabled={saving || !title.trim()}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Kanban = ({ projectId, readOnly = false, canWrite = true }) => {
  const { user, profile } = useAuth();
  const isAdmin = !readOnly && !!profile?.isAdmin;
  const [tasks, setTasks] = useState([]);
  const [addingCol, setAddingCol] = useState(null);
  const [editing, setEditing] = useState(null);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => subscribeTasks(projectId, setTasks), [projectId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const tasksByCol = useMemo(() => {
    const map = Object.fromEntries(COLUMNS.map((c) => [c.id, []]));
    tasks.forEach((t) => {
      const key = map[t.column] ? t.column : "backlog";
      map[key].push(t);
    });
    return map;
  }, [tasks]);

  const findColumnOfId = (id) => {
    if (COLUMNS.some((c) => c.id === id)) return id;
    const t = tasks.find((x) => x.id === id);
    return t?.column || null;
  };

  const handleDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    const activeTask = tasks.find((t) => t.id === active.id);
    if (!activeTask) return;

    const overCol = findColumnOfId(over.id);
    if (!overCol) return;

    // Build the new flat order by columns, inserting the active task at the right spot.
    const withoutActive = tasks.filter((t) => t.id !== active.id);

    // Decide insertion index inside the target column.
    let insertAt;
    if (COLUMNS.some((c) => c.id === over.id)) {
      // dropped onto empty column or the column body itself — append
      const target = withoutActive.filter((t) => t.column === overCol);
      insertAt = target.length;
    } else {
      const target = withoutActive.filter((t) => t.column === overCol);
      const overIdx = target.findIndex((t) => t.id === over.id);
      insertAt = overIdx < 0 ? target.length : overIdx;
    }

    // Reassemble: rebuild the full ordered list column by column.
    const next = [];
    for (const col of COLUMNS) {
      let colTasks = withoutActive.filter((t) => t.column === col.id);
      if (col.id === overCol) {
        colTasks = [...colTasks];
        colTasks.splice(insertAt, 0, { ...activeTask, column: overCol });
      }
      next.push(...colTasks);
    }

    // Optimistic local update
    setTasks(next);
    try {
      await reorderTasks(projectId, next, user?.uid);
    } catch (err) {
      console.error("Kanban reorder failed", err);
    }
  };

  const onAdd = (columnId) => setAddingCol(columnId);
  const onEdit = (task) => setEditing(task);
  const onDelete = async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    await deleteTask(projectId, task.id);
  };

  const saveNew = async ({ title, description }) => {
    await createTask(projectId, { title, description, column: addingCol, authorUid: user?.uid });
    setAddingCol(null);
  };

  const saveEdit = async ({ title, description }) => {
    await updateTask(projectId, editing.id, { title, description });
    setEditing(null);
  };

  const activeTask = activeId ? tasks.find((t) => t.id === activeId) : null;

  return (
    <div className="fw-kan">
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={(e) => setActiveId(e.active.id)}
        onDragCancel={() => setActiveId(null)}
        onDragEnd={handleDragEnd}
      >
        <div className="fw-kan__cols">
          {COLUMNS.map((col) => (
            <Column
              key={col.id}
              column={col}
              tasks={tasksByCol[col.id]}
              isAdmin={isAdmin}
              canWrite={canWrite}
              currentUid={user?.uid}
              onAdd={onAdd}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
        <DragOverlay>
          {activeTask ? (
            <div className="fw-kan__card fw-kan__card--ghost">
              <div className="fw-kan__card-title">{activeTask.title}</div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {addingCol && (
        <TaskModal onSave={saveNew} onClose={() => setAddingCol(null)} />
      )}
      {editing && (
        <TaskModal initial={editing} onSave={saveEdit} onClose={() => setEditing(null)} />
      )}
    </div>
  );
};

export default Kanban;
